import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { ApiCacheService } from '../services/api-cache.service';

const CACHEABLE_PATTERNS = [
  /loyalty-analytics\//,
  /loyalty-customer\/get-total-customer-count/,
  /dashboard\//,
  /overview\//,
  /rules\/get-/,
  /products\/get-/,
  /coupon\/get-/,
  /coupons\/(get|analytics|particular-analytics)/,
  /rewards\/get-/,
  /feedback\/get-/,
  /referral\/get-/,
];

const EXCLUDED_PATTERNS = [
  /outlet/,
  /user/,
  /auth/,
  /\/create/,
  /\/update/,
  /\/delete/,
];

/** Mutations must drop cached list/KPI POSTs so the UI does not serve stale data. */
const MUTATE_PATTERNS = [
  /\/create/,
  /\/update/,
  /\/delete/,
  /\/edit/,
  /\/duplicate/,
  /\/grant/,
];

/** Collapse identical in-flight POSTs so parallel subscribers share one network call. */
const inflightRequests = new Map<string, Observable<unknown>>();

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'POST') {
    return next(req);
  }

  const url = req.url;
  const apiCache = inject(ApiCacheService);

  if (MUTATE_PATTERNS.some((pattern) => pattern.test(url))) {
    return next(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          apiCache.invalidateAll();
        }
      }),
    );
  }

  if (EXCLUDED_PATTERNS.some((pattern) => pattern.test(url))) {
    return next(req);
  }

  if (!CACHEABLE_PATTERNS.some((pattern) => pattern.test(url))) {
    return next(req);
  }

  const body = req.body;
  const key = apiCache.buildKey(url, body);

  const cachedResponse = apiCache.get(key);
  if (cachedResponse !== null) {
    return of(new HttpResponse({ body: cachedResponse, status: 200, statusText: 'OK' }));
  }

  let outletId = '';
  if (body && typeof body === 'object') {
    outletId = (body as { outletId?: string }).outletId || '';
  }

  const existing = inflightRequests.get(key);
  if (existing) {
    return existing as ReturnType<typeof next>;
  }

  const shared$ = next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && event.status === 200) {
        apiCache.set(key, event.body, outletId);
      }
    }),
    finalize(() => inflightRequests.delete(key)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  inflightRequests.set(key, shared$);
  return shared$ as ReturnType<typeof next>;
};
